// src/components/wellfinder/wellMapEncoding.js
//
// THE SINGLE SOURCE FOR EVERY DETECTION ENCODING ON THE WELLFINDER PRODUCT MAP.
//
// Same contract as reviewMapEncoding.js, for the other map: the paint
// expressions in WellLayer.jsx and the on-map legend are both DERIVED from this
// object. A legend written by hand beside the paint is one that silently stops
// being true the first time someone tunes a colour, and a wrong legend is worse
// than none — it tells an analyst the blue dot means something it does not.
//
// ── WHY THE FILL IS THE VERDICT ──────────────────────────────────────────────
//
// Until the wellfinder_detections tile layer existed, the tile carried no
// adjudication status at all, so the paint keyed on match_status and 6,826
// pending wells were indistinguishable from 3,873 approved ones. `status`
// decides the colour now; match_status only breaks the tie among approvals.
//
// PENDING IS RECESSIVE, NOT HIDDEN. Grey with a white ring, so it reads as "not
// yet looked at" rather than as a weak result. Hiding it was considered and
// refused: at ~37% reviewed, a two-thirds-unreviewed corpus would render as a
// sparse one, which is the same absence-as-signal trap aoiPredicate's header
// records for Tier D.
//
// Colours are CSS literals because MapLibre paint takes colours, not classes.
// Named after their tokens where one exists so the correspondence stays visible:
// slate-400 #94a3b8 · emerald #22c55e · amber #f59e0b · blue-500 #3b82f6 ·
// orange-500 #f97316 · red #ef4444.

const BLACK = "#000000";

export const WELL_ENCODING = Object.freeze({
  // ── undecided ──────────────────────────────────────────────────────────────
  pending: {
    key: "pending",
    label: "Not reviewed",
    status: "pending",
    fill: "#94a3b8",        // slate-400
    stroke: "#ffffff",      // the halo ring — the one state that gets white
    strokeWidth: 2,
  },

  // ── approved, by what the registry says ────────────────────────────────────
  strong: {
    key: "strong",
    label: "Approved · strong match",
    status: "approved",
    matchStatus: "strong",
    fill: "#22c55e",
    stroke: BLACK,
    strokeWidth: 1.5,
  },
  candidate: {
    key: "candidate",
    label: "Approved · candidate match",
    status: "approved",
    matchStatus: "candidate",
    fill: "#f59e0b",
    stroke: BLACK,
    strokeWidth: 1.5,
  },
  // Approved with nothing in the registry to point at. NOT red: red means
  // rejected, and a well the registry has never heard of is a FINDING rather
  // than a fault — it is the case the product exists to surface.
  noMatch: {
    key: "noMatch",
    label: "Approved · no registry match",
    status: "approved",
    matchStatus: "unverified",
    fill: "#3b82f6",        // blue-500
    stroke: BLACK,
    strokeWidth: 1.5,
  },

  // ── escalated ──────────────────────────────────────────────────────────────
  // Shares qc_conflict's hue deliberately: both mean "a human must look".
  // qc_conflict is rare — 7 rows on 2026-09-26, visible to the adjudicating
  // org only — so the two rarely meet on one map.
  needsReview: {
    key: "needsReview",
    label: "Needs review",
    status: "needs_review",
    fill: "#f97316",        // orange-500
    stroke: BLACK,
    strokeWidth: 1.5,
  },

  // ── the negative finding ───────────────────────────────────────────────────
  // Drawn by its own layer, hollow with a red ring, because "we looked and there
  // is no well here" is not a weaker well — it is a different claim.
  rejected: {
    key: "rejected",
    label: "Rejected · no well",
    status: "rejected",
    fill: BLACK,
    stroke: "#ef4444",
    strokeWidth: 3,
  },
});

/**
 * Legend order is READING order, not the order the states were written.
 * Undecided first because it is most of the map, then the decided states from
 * strongest evidence to none, then the two exceptions.
 */
export const WELL_LEGEND_ITEMS = Object.freeze([
  WELL_ENCODING.pending,
  WELL_ENCODING.strong,
  WELL_ENCODING.candidate,
  WELL_ENCODING.noMatch,
  WELL_ENCODING.needsReview,
  WELL_ENCODING.rejected,
]);

const { pending, strong, candidate, noMatch, needsReview, rejected } = WELL_ENCODING;

/**
 * Fill for the core layer, which draws everything EXCEPT rejected.
 *
 * Verdict first, match second. `rejected` is absent by construction — the layer
 * filters it out and the rejected layer draws it — so this expression must
 * never grow a branch for it.
 */
export const detectedCoreColor = () => [
  "case",
  ["==", ["get", "status"], pending.status],     pending.fill,
  ["==", ["get", "status"], needsReview.status], needsReview.fill,
  [
    "match",
    ["coalesce", ["get", "match_status"], noMatch.matchStatus],
    strong.matchStatus,    strong.fill,
    candidate.matchStatus, candidate.fill,
    "qc_conflict",         needsReview.fill,
    // 'unverified' and anything this client does not recognise: approved with
    // no registry counterpart.
    noMatch.fill,
  ],
];

/** The white halo ring on pending, black on every decided state. */
export const detectedCoreStrokeColor = () => [
  "case",
  ["==", ["get", "status"], pending.status], pending.stroke,
  strong.stroke,
];

export const detectedCoreStrokeWidth = () => [
  "case",
  ["==", ["get", "status"], pending.status], pending.strokeWidth,
  strong.strokeWidth,
];

/** Paint for the rejected layer — hollow centre, red ring. */
export const rejectedCirclePaint = () => ({
  "circle-color": rejected.fill,
  "circle-stroke-color": rejected.stroke,
  "circle-stroke-width": rejected.strokeWidth,
  "circle-stroke-opacity": 1,
});

/**
 * The filters, here rather than in WellLayer so the layer that draws a state and
 * the legend that names it cannot disagree about which rows it covers.
 *
 * THE RED LAYER KEYS ON THE VERDICT, NOT ON qc_type. It filtered on
 * qc_type = 'no_well', the legacy equipment value, and the two are not the same
 * population: of six rejected records one carries a reject_reason other than
 * no_well_present, so a qc_type filter paints five of six and drops the odd one
 * silently.
 */
export const IS_REJECTED = ["==", ["get", "status"], rejected.status];
export const IS_NOT_REJECTED = [
  "!=", ["coalesce", ["get", "status"], ""], rejected.status,
];
